import React from 'react';

import { useCreateRole } from '@vlabs/api/artemy/client';
import { PlusIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button';
import { toast } from 'react-toastify';

import { RoleForm } from '@vlabs/monokit/entities/roles';
import type { CreateDefaultValues } from '@vlabs/monokit/entities/roles';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useCreateRoleTranslation } from './i18n';
import type { CreateRoleProps } from './types';

type CreateRoleButtonProps = CreateRoleProps & Omit<ButtonProps, 'onClick'>;

const displayName = 'CreateRoleButton';
const testid = buildTestid(displayName);

function CreateRoleButton({
  onCreateRole = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: CreateRoleButtonProps): React.JSX.Element {
  const { t } = useCreateRoleTranslation();
  const modal = useModal({ size: 'small' });

  const createRole = useCreateRole({
    mutation: {
      onSuccess: (data) => {
        toast.success(t('toast_success'));
        if (data.role_id) {
          onCreateRole?.(data.role_id);
        }
        modal.close();
      },
      onError: () => {
        toast.error(t('toast_error'));
      },
    },
  });

  const handleSubmit = async (values: CreateDefaultValues): Promise<void> => {
    await createRole.mutateAsync({ data: values });
  };

  return (
    <React.Fragment>
      {modal.wrap(
        <RoleForm
          context="create"
          onCancel={modal.close}
          onSubmit={handleSubmit}
        />,
      )}

      <Button
        icon={<PlusIcon />}
        onClick={modal.open}
        {...testid(dataTestId)}
        {...props}
      >
        {t('add_button')}
      </Button>
    </React.Fragment>
  );
}

CreateRoleButton.displayName = displayName;

export { CreateRoleButton };
